import { StorageKey } from "config/storage";

/**
 * Picks the implementation behind `@novorender/api` for this page load. The Vite
 * `novorender-api-engine-switch` plugin reads `isWasmEngineActive` before either
 * engine is imported, so keep its imports minimal. App code should not branch on
 * `isWasmEngineActive`: the engine is a drop-in, so detect what an API offers instead.
 */

export const WASM_ENGINE_QUERY_PARAM = "novolibWasm";

export enum Backend {
    Auto = "auto",
    WebGPU = "webgpu",
    WebGL2 = "webgl2",
}

export const BACKEND_OPTIONS = [
    { value: Backend.Auto, labelKey: "graphicsApiAuto" },
    { value: Backend.WebGPU, labelKey: "graphicsApiWebGpu" },
    { value: Backend.WebGL2, labelKey: "graphicsApiWebGl2" },
] as const;

/** `?novolibWasm=true|false` wins for the current page load only; otherwise the persisted setting decides. */
export function shouldUseWasmEngine(search: string, storedSetting: string | null): boolean {
    const param = new URLSearchParams(search).get(WASM_ENGINE_QUERY_PARAM);
    if (param === "true" || param === "false") {
        return param === "true";
    }
    return storedSetting === "true";
}

export function parseBackend(value: string | null): Backend {
    return Object.values(Backend).find((backend) => backend === value) ?? Backend.Auto;
}

function readLocalStorage(key: StorageKey): string | null {
    try {
        return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
        return null;
    }
}

export function getStoredBackend(): Backend {
    return parseBackend(readLocalStorage(StorageKey.ExperimentalEngineBackend));
}

/** Persisted settings as they were when this page loaded, i.e. what the running engine uses. */
export const startupEngineSettings = {
    enabled: readLocalStorage(StorageKey.ExperimentalEngine) === "true",
    backend: getStoredBackend(),
};

export const isWasmEngineActive = shouldUseWasmEngine(
    globalThis.location?.search ?? "",
    readLocalStorage(StorageKey.ExperimentalEngine),
);

export function isEngineReloadRequired(enabled: boolean, backend: Backend): boolean {
    return enabled !== startupEngineSettings.enabled || (enabled && backend !== startupEngineSettings.backend);
}

const ENGINE_BACKEND_LABELS: Record<string, string> = {
    [Backend.WebGPU]: "WebGPU",
    [Backend.WebGL2]: "WebGL 2",
};

/** The graphics API bim-viewer-engine actually renders with, from its `view.statistics.engine.gpuBackend`. */
export function engineBackendLabel(statistics: unknown): string | undefined {
    const engine = (statistics as { engine?: { gpuBackend?: unknown } } | undefined)?.engine;
    const backend = engine?.gpuBackend;
    if (typeof backend !== "string" || backend === "") {
        return undefined;
    }
    return ENGINE_BACKEND_LABELS[backend] ?? backend;
}
